import { PrescriptionMedicationStatus } from "@/Common/Models/Patient/PatientEnums";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { MedicalItemMedicationAndPrescriptionDosage } from "@/Patient/Models/MedicalSummary/MedicalItemMedicationAndPrescriptionDosage";
import { MedicationModel } from "@/Patient/Models/Medication/MedicationModel";
import { MedicationsStatus } from "@/Patient/Models/UI/MedicalNote/Enums/MedicalItem";
import { ActiveMedicationCareElement } from "../Models/CareElements/ActiveMedicationCareElement";
import { CareComponent } from "../Models/CareElements/CareComponent";
import { CareElement } from "../Models/CareElements/CareElement";
import { Dosage } from "../Models/CareElements/Dosage";
import { ICareComponentService } from "./ICareComponentService";

function toDosage(dosage: MedicalItemMedicationAndPrescriptionDosage): Dosage {
    return new Dosage({
        DosageString: dosage.DosageString,
        DosageFrequency: dosage.DosageFrequency,
        IfNeeded: dosage.IfNeeded,
        Duration: dosage.Duration,
        DisplayMode: dosage.DisplayMode,
        DosageServedQuantity: dosage.DosageServedQuantity,
        DosageServedDays: dosage.DosageServedDays,
        DosageServedFrequency: dosage.DosageServedFrequency,
        IsFakeDosage: dosage.IsFakeDosage,
        DosageUnit: dosage.DosageUnit,
        DosageTypeLookupId: dosage.DosageTypeLookupId
    });
}

function toActiveMedicationCareElement(med: MedicationModel): ActiveMedicationCareElement {
    return new ActiveMedicationCareElement({
        Id: med.Model.Id,
        Name: med.Model.Name,
        CeaseDate: med.Model.CeaseDate,
        RefillStartDate: med.Model.RefillStartDate,
        RefillEndDate: med.Model.RefillEndDate,
        Dosages: med.Model.Dosages?.toArray()?.map(toDosage),
        RefillQuantity: med.Model.RefillQuantity,
        InitialRefillQuantity: med.Model.InitialRefillQuantity,
        NumberOfRefill: med.Model.NumberOfRefill,
        SummaryDisplayStatus: med.Model.SummaryDisplayStatus,
        TotalDurationManualDays: med.Model.TotalDurationManualDays,
        IsCeased: med.IsCeased(),
        LegalStatus: med.Model.LegalStatus,
        TotalDurationManual: med.Model.TotalDurationManual,
        Form: med.Model.Form,
        DrugStrength: med.Model.DrugStrength,
        PrescriptionMedicationStatus: med.Model.PrescriptionStatusInt.toString() as PrescriptionMedicationStatus
    });
}

class ActiveMedicationCareComponentService implements ICareComponentService {
    public processCareComponent(careComponent: CareComponent, patientContactId: string, encounterDate: Date): void {
        const activeMedication = this.getActiveMedicationMedicalItems(patientContactId, encounterDate);
        careComponent.CareElements = this.getActiveMedicationCareElements(activeMedication);
    }

    private getActiveMedicationMedicalItems(patientContactId: string, encounterDate: Date): MedicationModel[] {
        const encounterDateTime = LuxonDateTimeParser.parse(encounterDate).endOf("day");

        const encounterIds = patientStore.PatientMedicalSummaryModels[patientContactId]
            ?.get("Encounters")
            .filter(p => LuxonDateTimeParser.parse(p.ConsultationDate) <= encounterDateTime)
            .map(p => p.Id)
            .toArray();

        return patientStore.PatientMedicalSummaryModels[patientContactId]
            ?.get("MedicalItemsMedicationAndPrescription")
            ?.filter(p => (p.Model.VisibleForDisplayStatuses === MedicationsStatus.Active || p.Model.VisibleForDisplayStatuses === MedicationsStatus.Expired)
                && encounterIds.includes(p.Model.RelatedEncounterId)
            )
            .toArray() ?? [];
    }

    private getActiveMedicationCareElements(activeMedication: Partial<MedicationModel>[]): CareElement[] {
        if (!activeMedication?.length)
            return [];

        return activeMedication.map(toActiveMedicationCareElement);
    }
}

export { ActiveMedicationCareComponentService };
